import React from 'react';
import { buildMenuGroups } from 'route/menu';
import accessLoginConfig from '../config/access-login.json';
import { dynamicRoutes, routesConfig } from '../layouts/app-main';

// Mapping menu id (level_menu_limited di DB) -> path route
// Canonical keys = nilai di database / localStorage
export const MENU_TO_PATH = {
  // Dashboard
  'dashboard-member': '/dashboard',
  'dashboard-profit': '/dashboard-profit',
  'dashboard-trx': '/dashboard-trx',
  // DB has dashboard-warn-trx but no CMS page yet — intentionally omitted

  // Monitoring
  'blacklist-customer': '/blacklist-customer',
  'blacklist-ip': '/blacklist-ip',
  'monitoring-otp': '/monitoring-otp',
  'monitoring-customer-limit': '/monitoring-customer-limit',
  'monitoring-customer-history-pin': '/monitoring-customer-history-pin',
  'monitoring-customer-wrong-pin': '/monitoring-customer-wrong-pin',
  // Customer Service (DB stores message-customer / message-guest, not "message")
  'message-customer': '/chat',
  'message-guest': '/chat-guest',
  feedback: '/feedback',
  notification: '/notifications',

  // Member
  customers: '/customers',
  'setting-spv': '/setting-spv',
  partners: '/partners',

  // Pengajuan
  'verification-customer': '/verification-customer',
  'request-limit': '/request-limit',
  // Wallet
  deposit: '/deposit',
  'deposit-method': '/payment-method-deposit',
  'payment-method-ppob': '/payment-method-ppob',
  'setting-bank-deposit': '/setting-bank-deposit',
  // PPOB
  'ppob-product-category': '/ppob-product-category',
  'ppob-product': '/ppob-product',
  'ppob-vendor': '/ppob-vendor',
  'ppob-product-vendor': '/product-vendor',
  'ppob-inject-label': '/inject-label',
  'product-inject-category': '/product-inject-category',
  'product-inject-whitelist-sn': '/product-inject-whitelist-sn',
  'product-paket-data-category': '/product-paket-data-category',
  'data-package-label': '/data-package-label',
  'product-voucher-category': '/product-voucher-category',
  'ppob-voucher-label': '/voucher-label',
  'product-voucher-product': '/product-voucher-product',
  'product-voucher-list-voucher': '/product-voucher-list-voucher',
  'digipos-account': '/digipos-account',
  'digipos-area': '/digipos-area',
  'digipos-margin': '/digipos-margin',
  'isimple-account': '/isimple-account',
  'ismple-area': '/isimple-area', // DB typo preserved as canonical key
  'isimple-margin': '/isimple-margin',
  'cuanku-account': '/cuanku-account',
  'cuanku-area': '/cuanku-area',
  'cuanku-margin': '/cuanku-margin',
  'money-transfer-bank': '/money-transfer-bank',
  'setting-markup-global': '/setting-markup-global',
  'setting-mark-up': '/setting-mark-up',
  'limit-bypass-emoney': '/limit-bypass-emoney',
  'emoney-margin': '/emoney-setting-margin', // DB key; route path differs
  // PPOB TRX
  'ppob-transaction': '/ppob-transaction',
  'report-transaction-inbox': '/report-transaction-inbox',
  'product-inject-inbox': '/product-inject-inbox',
  'report-innet-trx': '/report-innet-trx',
  'report-transfer-uang': '/report-transfer-uang',
  'report-transfer-saldo': '/report-transfer-saldo',
  // Konten
  'content-banner': '/content-banner',
  'content-blog': '/content-blog',
  'content-category': '/content-category',
  'content-slider': '/content-slider',
  'content-slider-app': '/content-slider-app',
  'content-video': '/content-video',
  'content-tutorial': '/content-tutorial',
  'static-content': '/static-content',
  // Report
  'report-customer-deposit-saldo': '/report-customer-deposit-saldo',
  'report-deposit': '/report-deposit',
  'report-mutasi-saldo': '/report-mutasi-saldo',
  'report-registrasi-customer': '/report-registrasi-customer',
  'report-customer-fraud': '/report-customer-fraud',
  'report-customer-markup-company': '/report-customer-markup-company',
  'report-customer-markup': '/report-customer-markup',
  'report-customer-program': '/report-customer-program',
  'report-customer-qris': '/report-customer-qris',
  'report-idpel-emoney': '/report-idpel-emoney',
  'report-group-tfsaldo': '/trx-group-tfsaldo', // DB key; route path differs
  'report-idpel-tfbank': '/report-idpel-tfbank',
  'report-ppob-pending-time': '/report-ppob-pending-time',
  'report-ppob-transaction': '/report-ppob-transaction',
  'report-webtopup-transaction': '/report-webtopup-transaction',
  'report-app-transaction': '/report-app-transaction',
  'report-customer-omset': '/report-customer-omset',
  'report-omset-product': '/report-omset-product',
  'report-komisi-spv': '/report-komisi-spv',
  'report-ppob-sale': '/report-ppob-sale',
  'report-performa-outlet': '/report-performa-outlet',
  'report-customer-emoney': '/report-customer-emoney',
  'report-laba-rugi-ppob': '/report-laba-rugi-ppob',
  'report-input-voucher-admin': '/report-input-voucher-admin',
  'export-tagihan': '/export-tagihan',
  'mutasi-bank': '/mutasi-bank',
  // Pengaturan
  'prefix-provider': '/prefix-provider',
  'setting-general': '/setting-general',
  user: '/user',
  'device-user': '/device-user',
  'user-permissions': '/user-permissions',
  'setting-cut-of-data': '/setting-cut-of-data',
  'setting-block-limit': '/setting-block-limit',
  'setting-rekomendasi-limit': '/setting-rekomenasi-limit', // DB key; route has typo "rekomenasi"
};

/**
 * Alias groups: canonical DB key -> alternate keys (route slug / legacy / typo).
 * Matching is bidirectional via expandMenuKeyAliases.
 */
export const PERMISSION_ALIAS_GROUPS = {
  'ppob-product-vendor': ['product-vendor'],
  'emoney-margin': ['emoney-setting-margin'],
  'ismple-area': ['isimple-area'],
  'report-group-tfsaldo': ['trx-group-tfsaldo'],
  'setting-rekomendasi-limit': ['setting-rekomenasi-limit'],
  'message-customer': ['message'],
  'report-ppob-transaction': ['report/transaction-ppob'],
};

// Flat map: any alias/legacy key -> canonical DB key
export const ACCESS_LOGIN_ALIAS_MAP = Object.fromEntries(
  Object.entries(PERMISSION_ALIAS_GROUPS).flatMap(([canonical, aliases]) =>
    aliases.map((alias) => [alias, canonical])
  )
);

// Reverse: canonical -> first alias (for expand)
const REVERSE_ALIAS_MAP = Object.fromEntries(
  Object.entries(PERMISSION_ALIAS_GROUPS).map(([canonical, aliases]) => [canonical, aliases[0]])
);

// Normalize any key to canonical DB level_menu_limited
export const normalizeMenuKey = (menuKey) => {
  if (!menuKey) return menuKey;
  return ACCESS_LOGIN_ALIAS_MAP[menuKey] || menuKey;
};

// Expand a menu key to include all equivalent forms (for permission matching)
export const expandMenuKeyAliases = (menuKey) => {
  if (!menuKey) return [];
  const keys = new Set([menuKey]);
  const canonical = normalizeMenuKey(menuKey);
  keys.add(canonical);

  const groupAliases = PERMISSION_ALIAS_GROUPS[canonical];
  if (groupAliases) {
    groupAliases.forEach((alias) => keys.add(alias));
  }

  // Also accept reverse lookup if key itself is canonical with aliases
  if (REVERSE_ALIAS_MAP[menuKey]) {
    keys.add(REVERSE_ALIAS_MAP[menuKey]);
  }
  if (PERMISSION_ALIAS_GROUPS[menuKey]) {
    PERMISSION_ALIAS_GROUPS[menuKey].forEach((alias) => keys.add(alias));
  }

  return Array.from(keys);
};

/** Resolve route path from a permission / menu key (handles aliases) */
export const getPathFromMenuKey = (menuKey) => {
  if (!menuKey) return null;
  if (MENU_TO_PATH[menuKey]) return MENU_TO_PATH[menuKey];
  const canonical = normalizeMenuKey(menuKey);
  if (MENU_TO_PATH[canonical]) return MENU_TO_PATH[canonical];
  for (const key of expandMenuKeyAliases(menuKey)) {
    if (MENU_TO_PATH[key]) return MENU_TO_PATH[key];
  }
  return null;
};

// =====================================================
// SUB-MENU ITEMS MAPPING
// Map sub-menu IDs to their parent menu ID
// This is used to check permission based on parent menu access
// =====================================================
const SUB_MENU_PARENT_MAP = {
  // Monitoring sub-menus
  'monitoring-customer-history-pin': 'monitoring-customer',
  'monitoring-customer-wrong-pin': 'monitoring-customer',
  // Customer Service sub-menus
  'message-customer': 'message',
  'message-guest': 'message',
  // PPOB sub-menus
  'ppob-inject-label': 'product-inject',
  'product-inject-whitelist-sn': 'product-inject',
  'data-package-label': 'product-paket-data',
  'ppob-voucher-label': 'product-voucher',
  'digipos-account': 'digipos',
  'digipos-area': 'digipos',
  'digipos-margin': 'digipos',
  'isimple-account': 'isimple',
  'isimple-area': 'isimple',
  'ismple-area': 'isimple', // DB typo key
  'isimple-margin': 'isimple',
  'cuanku-account': 'cuanku',
  'cuanku-area': 'cuanku',
  'cuanku-margin': 'cuanku',
  'setting-markup-global': 'markup',
  'setting-mark-up': 'markup',
  'emoney-margin': 'limit-bypass-emoney',
  'emoney-setting-margin': 'limit-bypass-emoney',
  // Content sub-menus
  'content-category': 'content-blog',
  'content-slider-app': 'content-slider',
  // Report sub-menus
  'trx-group-tfsaldo': 'report-transaksi',
  'report-group-tfsaldo': 'report-transaksi',
  'report-idpel-tfbank': 'report-transaksi',
  // Settings sub-menus
  'setting-bank-deposit': 'deposit',
};

// Get parent menu ID for a given menu ID (for sub-menu items)
export const getParentMenuIdFromMenuId = (menuId) => {
  return SUB_MENU_PARENT_MAP[menuId] || null;
};

// =====================================================
// CHILD ROUTES MAPPING
// Mapping child routes ke parent menu ID untuk permission check
// Structure: { "child_route_path": "parent_menu_id" }
// =====================================================
const CHILD_ROUTES_TO_PARENT = {
  // Chat routes (maps to message-customer and message-guest)
  '/chat': 'message-customer',
  '/chat-guest': 'message-guest',
  // Blog child routes
  '/blog/add': 'content-blog',
  '/blog/detail': 'content-blog',
  '/blog/edit': 'content-blog',
  // Banner child routes
  '/banner/add': 'content-banner',
  '/banner/detail': 'content-banner',
  '/banner/edit': 'content-banner',
  // Category child routes (also from /category/*)
  '/category/add': 'content-category',
  '/category/detail': 'content-category',
  '/category/edit': 'content-category',
  // Slider child routes
  '/slider/add': 'content-slider',
  '/slider/detail': 'content-slider',
  '/slider/edit': 'content-slider',
  // Slider App child routes (both path variants used in app)
  '/slider-app/add': 'content-slider-app',
  '/slider-app/detail': 'content-slider-app',
  '/slider-app/edit': 'content-slider-app',
  '/content-slider-app/add': 'content-slider-app',
  '/content-slider-app/detail': 'content-slider-app',
  '/content-slider-app/edit': 'content-slider-app',
  // Video child routes
  '/video/add': 'content-video',
  '/video/detail': 'content-video',
  '/video/edit': 'content-video',
  // Tutorial child routes
  '/tutorial/add': 'content-tutorial',
  '/tutorial/detail': 'content-tutorial',
  '/tutorial/edit': 'content-tutorial',
  // Tutorial (alternative path)
  '/content-tutorial/add': 'content-tutorial',
  '/content-tutorial/edit': 'content-tutorial',
  // Customer child routes
  '/customers/detail': 'customers',
  '/customers/edit': 'customers',
  '/customers/add': 'customers',
  // Users child routes
  '/user/add': 'user',
  '/user/detail': 'user',
  '/user/edit': 'user',
  '/users/detail': 'user',
  '/users/edit': 'user',
  // User permissions (explicit nested paths — no wildcard)
  '/user-permissions/add': 'user-permissions',
  '/user-permissions/edit': 'user-permissions',
  // Partner child routes
  '/partners/add': 'partners',
  '/partners/detail': 'partners',
  '/partners/edit': 'partners',
  // Deposit child routes
  '/deposit/add': 'deposit',
  '/deposit/detail': 'deposit',
  '/deposit/edit': 'deposit',
  // PPOB Product child routes
  '/ppob/product/detail': 'ppob-product',
  '/ppob/product/edit': 'ppob-product',
  '/ppob/product/add': 'ppob-product',
  '/ppob-product/detail': 'ppob-product',
  '/ppob-product/edit': 'ppob-product',
  '/ppob-product/add': 'ppob-product',
  // Product Vendor child routes (must match level_menu_limited: ppob-product-vendor)
  '/product-vendor/add': 'ppob-product-vendor',
  '/product-vendor/detail': 'ppob-product-vendor',
  '/product-vendor/edit': 'ppob-product-vendor',
  // List PPOB Vendor child routes
  '/list-ppob-vendor/detail': 'ppob-vendor',
  '/ppob-vendor/detail': 'ppob-vendor',
  // Transaction child routes
  '/ppob-transaction/detail': 'ppob-transaction',
  // Money Transfer Bank child routes
  '/money-transfer-bank/add': 'money-transfer-bank',
  '/money-transfer-bank/detail': 'money-transfer-bank',
  '/money-transfer-bank/edit': 'money-transfer-bank',
  // Voucher child routes
  '/list-voucher/add': 'product-voucher-list-voucher',
  '/list-voucher/edit': 'product-voucher-list-voucher',
  '/list-voucher/detail': 'product-voucher-list-voucher',
  // Voucher Product child routes
  '/product-voucher-product/add': 'product-voucher-product',
  '/product-voucher-product/edit': 'product-voucher-product',
  '/product-voucher-product/detail': 'product-voucher-product',
  // Payment Method child routes
  '/payment-method-ppob/add': 'payment-method-ppob',
  '/payment-method-ppob/detail': 'payment-method-ppob',
  '/payment-method-ppob/edit': 'payment-method-ppob',
  '/payment-method-deposit/add': 'deposit-method',
  '/payment-method-deposit/detail': 'deposit-method',
  '/payment-method-deposit/edit': 'deposit-method',
  '/payment-method-webtopup/add': 'deposit-method-webtopup',
  '/payment-method-webtopup/detail': 'deposit-method-webtopup',
  '/payment-method-webtopup/edit': 'deposit-method-webtopup',
  // Verifikasi child routes
  '/verifikasi/detail': 'verification-customer',
  '/verification-customer/detail': 'verification-customer',
  // Innet Transaction child routes
  '/innet-transaction/detail': 'report-innet-trx',
  '/report-innet-trx/detail': 'report-innet-trx',
  // Notification child routes
  '/notifications/detail': 'notification',
  // Report Omset child routes
  '/report/report-detail-omset': 'report-omset-product',
  // Alternate PPOB transaction report path
  '/report/transaction-ppob': 'report-ppob-transaction',
  // Promo child routes
  '/kupon/detail': 'ppob-promo',
  '/kupon/used': 'ppob-promo',
};

// =====================================================
// EXPORT CHILD ROUTES MAPPING for external use
// =====================================================
export { CHILD_ROUTES_TO_PARENT };
export const getParentMenuIdFromPath = (path) => {
  if (!path) return null;
  const cleanPath = normalizePath(path.split('?')[0].split('#')[0]);

  // 1. Check exact match in CHILD_ROUTES_TO_PARENT
  if (CHILD_ROUTES_TO_PARENT[cleanPath]) {
    return normalizeMenuKey(CHILD_ROUTES_TO_PARENT[cleanPath]);
  }

  // 2. Check pattern match (e.g., /blog/detail/123 -> /blog/detail)
  const pathParts = cleanPath.split('/');
  for (let i = pathParts.length - 1; i >= 2; i--) {
    const tryPath = pathParts.slice(0, i).join('/');
    if (CHILD_ROUTES_TO_PARENT[tryPath]) {
      return normalizeMenuKey(CHILD_ROUTES_TO_PARENT[tryPath]);
    }
  }

  // 3. Try to match with dynamic routes - find parent menu by static path prefix
  for (const routePath of [
    ...routesConfig.map((r) => r.path),
    ...dynamicRoutes.map((r) => r.path),
  ]) {
    const staticPath = routePath.split('/:')[0];
    if (staticPath && (cleanPath === staticPath || cleanPath.startsWith(staticPath + '/'))) {
      const menuId = PATH_TO_MENU_ID[routePath] || PATH_TO_MENU_ID[staticPath];
      if (menuId) return normalizeMenuKey(menuId);
    }
  }

  return null;
};

// =====================================================
// HELPER: Check if path is a child route
// =====================================================
export const isChildRoute = (path) => {
  if (!path) return false;
  const cleanPath = path.split('?')[0].split('#')[0];

  for (const childPath of Object.keys(CHILD_ROUTES_TO_PARENT)) {
    if (cleanPath === childPath || cleanPath.startsWith(childPath + '/')) {
      return true;
    }
  }

  return false;
};

// =====================================================
// HELPER: Get all candidate menu IDs to check for a path
// =====================================================
export const getMenuIdCandidates = (menuId, menuPath) => {
  const candidates = new Set();

  if (menuId) {
    candidates.add(menuId);
    candidates.add(menuId.replace(/-/g, '_'));
    candidates.add(menuId.replace(/_/g, '-'));
  }

  if (menuPath) {
    candidates.add(menuPath);
    candidates.add(menuPath.startsWith('/') ? menuPath : `/${menuPath}`);
    candidates.add(menuPath.replace(/^\/+/, ''));
    candidates.add(menuPath.replace(/^\/+/, '').replace(/-/g, '_'));
    candidates.add(menuPath.replace(/^\/+/, '').replace(/_/g, '-'));
  }

  // Parent menu variants (e.g., content-blog -> content)
  if (menuId) {
    const parts = menuId.split('-');
    if (parts.length > 1) {
      candidates.add(parts[0]);
      candidates.add('/' + parts[0]);
    }
  }

  return candidates;
};

const ALL_ACTIONS = 63; // Create + Read + Update + Delete + Export + Import

// Action label to bitmask mapping
const ACTION_BITMASK = {
  C: 1,
  R: 2,
  U: 4,
  D: 8,
  E: 16,
  I: 32,
};

// Parse action string (e.g., "C,R,U") to bitmask
const parseActionString = (actString = '') => {
  if (!actString) return 0;
  let bitmask = 0;
  actString.split(',').forEach((a) => {
    const label = a.trim().toUpperCase();
    if (ACTION_BITMASK[label]) {
      bitmask |= ACTION_BITMASK[label];
    }
  });
  return bitmask;
};

// Build a map of menuId -> act (from menu.jsx)
const buildMenuActMap = () => {
  const menuActMap = {};
  const groups = buildMenuGroups();
  const collectActs = (items) => {
    (items || []).forEach((item) => {
      if (item.id && item.act) {
        menuActMap[item.id] = item.act;
      }
      if (item.subs && item.subs.length > 0) {
        collectActs(item.subs);
      }
    });
  };
  groups.forEach((g) => collectActs(g.items));
  return menuActMap;
};

let cachedMenuActMap = null;
const getMenuActMap = () => {
  if (!cachedMenuActMap) {
    cachedMenuActMap = buildMenuActMap();
  }
  return cachedMenuActMap;
};

const normalizePath = (path) => {
  if (!path) return '';
  const clean = path.split('?')[0].split('#')[0];
  return clean.startsWith('/') ? clean : `/${clean}`;
};

// Konversi menu id ke path (untuk pengecekan akses di menuPermissionMap.jsx)
// Ini return menu ID (kebab-case) sesuai dengan level_menu_limited di database
export const getPermissionKey = (menuId) => {
  if (!menuId) return menuId;
  // Return menu ID as-is (kebab-case format)
  return menuId;
};

// Reverse mapping dari path ke menu id
const PATH_TO_MENU_ID = Object.fromEntries(
  Object.entries(MENU_TO_PATH).map(([id, path]) => [path, id])
);

// Get menu id from route path (untuk cek akses menu)
export const getMenuIdFromPath = (path) => {
  if (!path) return null;
  const cleanPath = normalizePath(path);

  // 1. Exact path -> menu id
  if (PATH_TO_MENU_ID[cleanPath]) {
    return PATH_TO_MENU_ID[cleanPath];
  }

  // 2. Child route (e.g. /product-vendor/detail/123 -> ppob-product-vendor)
  const parentMenuId = getParentMenuIdFromPath(cleanPath);
  if (parentMenuId) {
    return normalizeMenuKey(parentMenuId);
  }

  // 3. Prefix match against known menu paths (e.g. /product-vendor/foo -> ppob-product-vendor)
  for (const [menuId, menuPath] of Object.entries(MENU_TO_PATH)) {
    if (cleanPath === menuPath || cleanPath.startsWith(`${menuPath}/`)) {
      return menuId;
    }
  }

  // Fallback: strip leading slash and return as-is
  return cleanPath.replace(/^\/+/, '');
};

export const parseAccessLoginForLevel = (userLevelName) => {
  if (!userLevelName || !accessLoginConfig) return [];

  const normalizedLevel = userLevelName.toLowerCase().trim();
  const menuActMap = getMenuActMap();
  const menuPermissions = [];

  // Build a set of valid menu IDs for validation
  const validMenuIds = new Set(Object.keys(MENU_TO_PATH));

  Object.entries(accessLoginConfig).forEach(([menuKey, allowedRoles]) => {
    // Parse roles - each element is a comma-separated string
    const rolesArray = (allowedRoles || [])
      .flat()
      .map((role) => role.split(',').map((r) => r.trim().toLowerCase()))
      .flat();

    if (rolesArray.includes(normalizedLevel)) {
      if (menuKey) {
        // Normalize menu key (handle aliases like 'product-vendor' -> 'ppob-product-vendor')
        const normalizedMenuKey = normalizeMenuKey(menuKey);

        // Skip if not a valid menu ID
        if (!validMenuIds.has(normalizedMenuKey)) {
          console.warn(`Menu key "${menuKey}" not found in MENU_TO_PATH, skipping...`);
          return;
        }

        // Get the actual allowed actions from menu.jsx act field
        const menuAct = menuActMap[normalizedMenuKey] || 'R';
        const actions = parseActionString(menuAct);
        menuPermissions.push({ menu: normalizedMenuKey, actions: actions });
      }
    }
  });

  return menuPermissions;
};

export const getAllRolesFromAccessLogin = () => {
  if (!accessLoginConfig) return [];

  const rolesSet = new Set();
  Object.values(accessLoginConfig).forEach((allowedRoles) => {
    allowedRoles.forEach((roleStr) => {
      roleStr.split(',').forEach((r) => {
        const trimmed = r.trim();
        if (trimmed) rolesSet.add(trimmed);
      });
    });
  });
  return Array.from(rolesSet).sort();
};

let cachedMenuItems = null;

const flattenMenuItems = (items, group, acc, parentPath = '') => {
  (items || []).forEach((item) => {
    const itemPath = parentPath ? `${parentPath} / ${item.label}` : item.label;
    acc.push({ id: item.id, label: itemPath, to: item.to || '', group });
    if (item.subs && item.subs.length > 0) {
      flattenMenuItems(item.subs, group, acc, itemPath);
    }
  });
  return acc;
};

// const routeAliases = [
//   { prefix: '/users', menuId: 'user' },
//   { prefix: '/verifikasi', menuId: 'verification-customer' },
//   { prefix: '/kupon', menuId: 'ppob-promo' },
//   { prefix: '/ppob/product', menuId: 'ppob-product' },
//   { prefix: '/list-ppob-vendor', menuId: 'ppob-vendor' },
//   { prefix: '/product-vendor', menuId: 'ppob-product-vendor' },
//   { prefix: '/web-transaction', menuId: 'report-webtopup-transaction' },
//   { prefix: '/app-transaction', menuId: 'report-app-transaction' },
//   { prefix: '/list-voucher', menuId: 'product-voucher-list-voucher' },
//   { prefix: '/banner', menuId: 'content-banner' },
//   { prefix: '/blog', menuId: 'content-blog' },
//   { prefix: '/slider', menuId: 'content-slider' },
//   { prefix: '/video', menuId: 'content-video' },
//   { prefix: '/innet-transaction', menuId: 'report-innet-trx' },
//   { prefix: '/report/report-detail-omset', menuId: 'report-omset-product' },
//   { prefix: '/report/transaction-ppob', menuId: 'report-ppob-transaction' },
//   { prefix: '/trx-group-tfsaldo', menuId: 'report-group-tfsaldo' },
//   { prefix: '/ppob-promo', menuId: 'ppob-promo' },
//   { prefix: '/monitoring-customer-wrong-pin', menuId: 'monitoring-customer-wrong-pin' },
// ];

// const getAliasMenuId = (path) => {
//   const match = routeAliases.find(
//     (alias) => path === alias.prefix || path.startsWith(alias.prefix + '/')
//   );
//   return match ? match.menuId : null;
// };

export const getAllMenuItems = () => {
  if (cachedMenuItems) return cachedMenuItems;
  const groups = buildMenuGroups();
  const acc = [];
  groups.forEach((group) => flattenMenuItems(group.items, group.title, acc));
  cachedMenuItems = acc;
  return cachedMenuItems;
};

// Generate GRANT_ALL_PERMISSIONS dari menu items yang ada
export const getGrantAllPermissions = () => {
  const items = getAllMenuItems().filter((item) => item.to);
  return items.map((item) => ({
    menu: item.to,
    actions: ALL_ACTIONS,
  }));
};

export const getMenuOptions = (selectedMenus = []) => {
  const items = getAllMenuItems().filter((item) => item.to);
  const seen = new Set();
  const groupedOptions = {};

  items.forEach((item) => {
    const value = item.to;
    if (selectedMenus.includes(value)) return;
    if (seen.has(value)) return;
    seen.add(value);

    const group = item.group || 'Other';
    if (!groupedOptions[group]) groupedOptions[group] = [];
    groupedOptions[group].push({ label: item.label, value });
  });

  return groupedOptions;
};

// Get grouped menus with nested structure (for permission UI)
export const getMenuGroupsWithSubs = () => {
  const groups = buildMenuGroups();
  return groups.map((group) => {
    const flattenItems = (items, parentLabel = '') => {
      let result = [];
      items?.forEach((item) => {
        const fullLabel = parentLabel ? `${parentLabel} / ${item.label}` : item.label;
        if (item.to) {
          // Use menu id as value (for level_menu_limited in database)
          const menuId = item.id;
          result.push({
            id: item.id,
            label: item.label,
            fullLabel: fullLabel,
            value: menuId, // Use menu id as value (level_menu_limited)
            displayValue: item.to.replace(/^\/+/, ''), // Path for UI display
            parentLabel: parentLabel,
            act: item.act,
            permission: item.id,
          });
        }
        if (item.subs && item.subs.length > 0) {
          result = result.concat(flattenItems(item.subs, fullLabel));
        }
      });
      return result;
    };

    return {
      title: group.title,
      items: flattenItems(group.items),
    };
  });
};

export const getMenuOptionsFlat = () => {
  const items = getAllMenuItems().filter((item) => item.to);
  const seen = new Set();
  const options = [];

  items.forEach((item) => {
    const value = item.to;
    if (seen.has(value)) return;
    seen.add(value);
    const label = item.group ? `${item.group} / ${item.label}` : item.label;
    options.push({ label, value });
  });

  return options;
};

// Get the first allowed route based on user permissions
const getPermissionMenuKey = (permission) => {
  return (
    permission?.level_menu_limited ||
    permission?.level_menu ||
    permission?.menu ||
    permission?.path ||
    ''
  )
    .toString()
    .trim();
};

export const getFirstAllowedRoute = (permissions) => {
  if (!permissions || permissions.length === 0) return null;

  // If only one permission and it's not dashboard, return it directly
  if (permissions.length === 1) {
    const perm = getPermissionMenuKey(permissions[0]);
    if (perm) {
      const mapped = getPathFromMenuKey(perm);
      if (mapped) return mapped;
      return perm.startsWith('/') ? perm : `/${perm}`;
    }
    return null;
  }

  const allowedPaths = new Set();

  // Build set of all allowed paths from user permissions
  permissions.forEach((p) => {
    const perm = getPermissionMenuKey(p);
    if (!perm) return;

    expandMenuKeyAliases(perm).forEach((key) => {
      allowedPaths.add(key);
      allowedPaths.add(key.startsWith('/') ? key.slice(1) : key);
      allowedPaths.add(key.startsWith('/') ? key : `/${key}`);
      allowedPaths.add(key.replace(/-/g, '_'));
      allowedPaths.add(key.replace(/_/g, '-'));
    });

    const mappedPath = getPathFromMenuKey(perm);
    if (mappedPath) {
      allowedPaths.add(mappedPath);
      allowedPaths.add(mappedPath.replace(/^\/+/, ''));
    }
  });

  // Priority order of routes (most important first) - with variants
  const priorityRoutes = [
    // Dashboard variants
    { path: '/dashboard', aliases: ['dashboard', '/dashboard-member', 'dashboard-member'] },
    { path: '/dashboard-profit', aliases: ['dashboard-profit', 'dashboard_profit'] },
    { path: '/dashboard-trx', aliases: ['dashboard-trx', 'dashboard_trx'] },
    // Common routes
    { path: '/customers', aliases: ['customers', '/customers'] },
    {
      path: '/chat',
      aliases: ['chat', '/chat', 'message', '/message', 'message-customer', 'message-guest'],
    },
    { path: '/deposit', aliases: ['deposit', '/deposit'] },
    { path: '/ppob-transaction', aliases: ['ppob-transaction', 'ppob_transaction'] },
    { path: '/report-customer-omset', aliases: ['report-customer-omset', 'report_customer_omset'] },
    // Konten routes (for users with only konten access)
    { path: '/content-blog', aliases: ['content-blog', 'content_blog', '/content-blog'] },
    { path: '/content-banner', aliases: ['content-banner', 'content_banner', '/content-banner'] },
    { path: '/content-slider', aliases: ['content-slider', 'content_slider'] },
    { path: '/content-video', aliases: ['content-video', 'content_video'] },
    { path: '/content-tutorial', aliases: ['content-tutorial', 'content_tutorial'] },
    { path: '/content-category', aliases: ['content-category', 'content_category'] },
    { path: '/content-slider-app', aliases: ['content-slider-app', 'content_slider_app'] },
    { path: '/static-content', aliases: ['static-content', 'static_content'] },
  ];

  // Find first allowed route from priority list
  for (const route of priorityRoutes) {
    if (allowedPaths.has(route.path) || route.aliases.some((alias) => allowedPaths.has(alias))) {
      return route.path;
    }
  }

  // If no priority route found, get the first permission path
  const firstPerm = getPermissionMenuKey(permissions[0]);
  if (firstPerm) {
    const mapped = getPathFromMenuKey(firstPerm);
    if (mapped) return mapped;
    return firstPerm.startsWith('/') ? firstPerm : `/${firstPerm}`;
  }

  return null;
};

export const isKnownRoute = (path) => {
  if (!path) return false;
  const cleanPath = normalizePath(path);

  // Explicit nested routes (structured — no prefix wildcard)
  const explicitNestedPatterns = [
    /^\/user-permissions$/,
    /^\/user-permissions\/add$/,
    /^\/user-permissions\/edit\/[^/]+$/,
  ];
  if (explicitNestedPatterns.some((re) => re.test(cleanPath))) {
    return true;
  }

  // Known child route prefixes from CHILD_ROUTES_TO_PARENT
  for (const childPath of Object.keys(CHILD_ROUTES_TO_PARENT)) {
    if (cleanPath === childPath || cleanPath.startsWith(`${childPath}/`)) {
      return true;
    }
  }

  const allRoutes = [...routesConfig, ...dynamicRoutes];

  // Exact match
  if (allRoutes.some((route) => cleanPath === route.path)) {
    return true;
  }

  // Dynamic route patterns (e.g. /users/edit/:id -> /users/edit/123)
  return allRoutes.some((route) => {
    if (!route.path.includes(':')) return false;
    const pattern = route.path.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(cleanPath);
  });
};
